import type { ReactNode } from "react";
import InlineMarkdown from "./InlineMarkdown";
import StreamingCursor from "./StreamingCursor";
import { parseTable, type HeadingLevel, type MarkdownSegment } from "./utils";

type MarkdownBlockProps = {
  segment: MarkdownSegment;
  showCursor: boolean;
};

const headingClasses: Record<HeadingLevel, string> = {
  1: "text-lg font-semibold leading-snug text-gray-900",
  2: "text-base font-semibold leading-snug text-gray-900",
  3: "text-sm font-semibold leading-snug text-gray-900",
  4: "text-sm font-semibold leading-snug text-gray-800",
};

function renderHeading(
  level: HeadingLevel,
  children: ReactNode,
  className: string,
) {
  if (level === 1) return <h1 className={className}>{children}</h1>;
  if (level === 2) return <h2 className={className}>{children}</h2>;
  if (level === 3) return <h3 className={className}>{children}</h3>;
  return <h4 className={className}>{children}</h4>;
}

function MarkdownTable({ lines }: { lines: string[] }) {
  const { header, body } = parseTable(lines);

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50">
          <tr>
            {header.map((heading, cellIndex) => (
              <th
                key={cellIndex}
                className="whitespace-nowrap px-3 py-2 text-left font-semibold text-gray-700"
              >
                <InlineMarkdown text={heading} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}
            >
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className="whitespace-nowrap px-3 py-2 text-gray-700"
                >
                  <InlineMarkdown text={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MarkdownBlock({
  segment,
  showCursor,
}: MarkdownBlockProps) {
  if (segment.type === "heading") {
    return (
      <div>
        {renderHeading(
          segment.level,
          <>
            <InlineMarkdown text={segment.text} />
            {showCursor && <StreamingCursor />}
          </>,
          headingClasses[segment.level],
        )}
      </div>
    );
  }

  if (segment.type === "table") {
    return (
      <div>
        <MarkdownTable lines={segment.lines} />
        {showCursor && <StreamingCursor />}
      </div>
    );
  }

  if (segment.type === "unordered-list") {
    return (
      <ul className="list-disc space-y-1 pl-5">
        {segment.items.map((item, itemIndex) => (
          <li key={itemIndex}>
            <InlineMarkdown text={item} />
            {showCursor && itemIndex === segment.items.length - 1 && (
              <StreamingCursor />
            )}
          </li>
        ))}
      </ul>
    );
  }

  if (segment.type === "ordered-list") {
    return (
      <ol className="list-decimal space-y-1 pl-5">
        {segment.items.map((item, itemIndex) => (
          <li key={itemIndex}>
            <InlineMarkdown text={item} />
            {showCursor && itemIndex === segment.items.length - 1 && (
              <StreamingCursor />
            )}
          </li>
        ))}
      </ol>
    );
  }

  if (segment.type === "code") {
    return (
      <div>
        <pre className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-800">
          <code
            className={segment.language ? `language-${segment.language}` : ""}
          >
            {segment.content}
          </code>
        </pre>
        {showCursor && <StreamingCursor />}
      </div>
    );
  }

  return (
    <p className="whitespace-pre-wrap leading-relaxed">
      <InlineMarkdown text={segment.content} />
      {showCursor && <StreamingCursor />}
    </p>
  );
}
